import classnames from 'classnames';
import { isEqual, isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { getAvailableDocumentNames } from '../../../admin/documents/store/selectors';
import { Icon } from '../../../shared/icon/Icon';
import { Agency, Bookshelf, Date, Document, Engine, OfficeBuilding, Robot, SaveFile, Tick, User } from '../../../shared/icons';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import styles from '../Reports.module.scss';
import { DocumentMetaReportField, getReportMetaFields, getSelectedDocumentNameIds, setDocumentReportMetaFields } from '../store';
import { NETTING_ENGINE_DOCUMENT_NAME_IDS } from '../../store/types';
import { Toggle } from '../../../shared/toggle';

const { green } = styles;

export const MetaFields: React.FC = () => {
    const dispatch = useAppDispatch();
    const selectedMetaFields = useAppSelector(getReportMetaFields);

    const allDocumentNames = useAppSelector(getAvailableDocumentNames);
    const selectedDocumentNameIds = useAppSelector(getSelectedDocumentNameIds);

    const selectedDocumentNames = useMemo(() => allDocumentNames.filter(({ datasetId }) => datasetId).filter(({ documentNameId }) => selectedDocumentNameIds.includes(documentNameId!.toString())), [allDocumentNames, selectedDocumentNameIds]);

    const selectedAgreementType = useMemo(() => selectedDocumentNames[0]?.agreementType || null, [selectedDocumentNames]);
    const isNettingSummaryType = useMemo(() => selectedDocumentNameIds.some(documentNameId => NETTING_ENGINE_DOCUMENT_NAME_IDS.includes(parseInt(documentNameId))), [selectedDocumentNameIds]);
    const selectedDocumentNameTypes = useMemo(() => selectedDocumentNames.map(({ documentName }) => documentName), [selectedDocumentNames]);

    const allMetaFields = useMemo(() => {
        let metaFields = [
            {
                field: DocumentMetaReportField.DESCRIPTION,
                description: 'The name of the document saved in Ark51. If this was never modified then this will match the filename of the document as it was uploaded.',
                icon: SaveFile
            },
            {
                field: DocumentMetaReportField.LATEST_AMENDMENT_DESCRIPTION,
                description: 'The name of the latest amendment/restatement document saved in Ark51 (if there are any amendments). If this was never modified then this will match the filename of the document as it was uploaded.',
                icon: SaveFile
            },
            {
                field: DocumentMetaReportField.AGREEMENT_TYPE,
                description: `The type of agreement the report is being generated for.${!isNull(selectedAgreementType) ? ` In this case it would always be ${selectedAgreementType}.` : ''}`,
                icon: Bookshelf
            },
            {
                field: DocumentMetaReportField.DOCUMENT_NAME,
                description: `The document type within this agreement type. This will always be one of the selected Document Types selected on previous tab.${selectedDocumentNameTypes.length > 0 ? ` For Example: ${selectedDocumentNameTypes[0]}.` : ''}`,
                icon: Document
            },
            {
                field: DocumentMetaReportField.ENTITY_A,
                description: 'The name of the entity which is set to be Party A for the document',
                icon: OfficeBuilding
            },
            {
                field: DocumentMetaReportField.ENTITY_B,
                description: 'The name of the entity which is set to be Party B for the document',
                icon: OfficeBuilding
            },
            {
                field: DocumentMetaReportField.EXECUTED_DATE,
                description: 'The date of execution of the latest document in the timeline',
                icon: Date
            },
            {
                field: DocumentMetaReportField.DATED_AS_OF_DATE,
                description: 'The dated as of date of the latest document in the timeline',
                icon: Date
            },
            {
                field: DocumentMetaReportField.CREATED_BY,
                description: 'The name of the user that initially uploaded the document',
                icon: User
            },
            {
                field: DocumentMetaReportField.LAST_MODIFIED_BY,
                description: 'The name of the user that most recently amended the document',
                icon: User
            },
            {
                field: DocumentMetaReportField.AGENCY,
                description: 'A column to indicate if the document is an Agency agreement',
                icon: Agency
            },
            {
                field: DocumentMetaReportField.EXTRACTED_WITH_AI,
                description: 'A column to indicate if the document was uploaded and completed with AI',
                icon: Robot
            },
            {
                field: DocumentMetaReportField.INSTANCE_COMPLETE,
                description: 'A column to indicate if the latest document in the timeline has been published with all information completed',
                icon: Tick
            }
        ];
        if (isNettingSummaryType) {
            metaFields.push({
                field: DocumentMetaReportField.NETTING_SUMMARY,
                description: 'A column to indicate the overall netting analysis summary for the document as it stands. Will only be included for ISDA documents.',
                icon: Engine
            });
        }
        return metaFields;
    }, [selectedAgreementType, selectedDocumentNameTypes, isNettingSummaryType]);
    const getIsSelected = useCallback((metaField: DocumentMetaReportField) => selectedMetaFields.includes(metaField), [selectedMetaFields]);

    const toggleMetaField = useCallback((metaField: DocumentMetaReportField) => {
        const isSelected = getIsSelected(metaField);
        const updatedMetaFields = isSelected ? selectedMetaFields.filter(includedMetaField => includedMetaField !== metaField) : [...selectedMetaFields, metaField];
        dispatch(setDocumentReportMetaFields(updatedMetaFields));
    }, [getIsSelected, selectedMetaFields, dispatch]);

    const allMetaFieldsSelected = useMemo(() => isEqual(selectedMetaFields, allMetaFields.map(({ field }) => field)), [selectedMetaFields, allMetaFields]);

    const toggleAllMetaFields = useCallback((checked: boolean) => {
        const updatedMetaFields = checked ? allMetaFields.map(({ field }) => field) : [];
        dispatch(setDocumentReportMetaFields(updatedMetaFields));
    }, [dispatch, allMetaFields]);

    return (
        <div className={styles.metaFieldsWrapper}>
            <div className={styles.metaFieldsHeader} data-testid='document-report-meta-fields'>
                <div className={styles.metaFieldsTitle}>Select the information you would like to retrieve from the Document</div>
                <div className={styles.selectAllWrapper}>
                    <div className={styles.selectAllLabel}>Select All</div>
                    <Toggle onChange={toggleAllMetaFields} checked={allMetaFieldsSelected} onColour={green} />
                </div>
            </div>
            <div className={styles.allMetaFieldsWrapper}>
                <Scrollable>
                    <div className={styles.allMetaFields}>
                        {allMetaFields.map(({ field, description, icon }, index) => {
                            const isSelected = getIsSelected(field);
                            return (
                                <button key={index} className={classnames(styles.metaReportField, { [styles.selectedMetaReportField]: isSelected })} onClick={() => toggleMetaField(field)}>
                                    <div className={styles.iconWrapper}><Icon icon={icon} fontSize={40} /></div>
                                    <div className={styles.infoWrapper}>
                                        <div className={styles.metaField}>{field}</div>
                                        <div className={styles.description}>{description}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </Scrollable>
            </div>
        </div>
    );
};
